import React, { useEffect, useRef } from 'react';
import { StarIcon, StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import user from '../assets/images/Frame 440.png';
import toJalali from '../utils/Time';

// Interfaces
export interface Review {
  review_id: string;
  user_name: string;
  rate: number;
  comment: string;
  date: string;
}

interface ReviewsSectionProps {
  reviewSummary?: string;
  reviews: Review[]; // تغییر دادیم به آرایه ساده چون در پدر مدیریت می‌شود
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

// Helper Component: Star Rating
const StarRating = ({ rating, user_name, date }: { rating: number, user_name: string, date: string }) => {
  return (
    <div className="flex justify-between font-myYekanFaNumRegular mb-2">
      <div className='flex flex-row gap-2'>
        <img src={user} alt="user" className="h-8 w-8 rounded-full" />
        <div className="flex flex-col gap-1">
          <span className="font-myYekanFaNumDemiBold text-sm">
            {user_name === "comment" || user_name === "Unknown" ? "کاربر سایت" : user_name}
          </span>
          <span className="text-gray-400 text-sm">{date}</span>
        </div>
      </div>
      <div className=''>
        <div className="flex items-center">
          {[1, 2, 3, 4, 5].map((star) => {
            if (star <= Math.floor(rating)) {
              return <StarIconSolid key={star} className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />;
            } else if (star === Math.ceil(rating) && rating % 1 !== 0) {
              return (
                <div key={star} className="relative h-4 w-4 sm:h-5 sm:w-5">
                  <StarIcon className="absolute h-4 w-4 sm:h-5 sm:w-5 text-gray-300" />
                  <div className="absolute overflow-hidden" style={{ width: `${(rating % 1) * 100}%` }}>
                    <StarIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                  </div>
                </div>
              );
            } else {
              return <StarIcon key={star} className="h-4 w-4 sm:h-5 sm:w-5 text-gray-300" />;
            }
          })}
        </div>
      </div>
    </div>
  );
};

// Main Component
const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  reviews,
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}) => {
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll observer (Only for Desktop Vertical List)
  useEffect(() => {
    // اگر در حال لود کردن هستیم یا به آخر صفحه رسیده‌ایم، observer را فعال نکن
    if (isLoading || currentPage >= totalPages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onPageChange(currentPage + 1);
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [currentPage, totalPages, isLoading, onPageChange]);

  // Skeleton Loader for Mobile & Tablet
  const renderMobileSkeletons = () => {
    return Array.from({ length: 2 }).map((_, index) => (
      <div key={`skeleton-mobile-${index}`} className="min-w-[313px] w-[343px] h-[200px] rounded-2xl border border-gray-200 p-[16px] flex flex-col justify-between shadow-sm bg-white animate-pulse">
        <div className="flex justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gray-200"></div>
            <div className="h-4 w-24 bg-gray-200 rounded"></div>
          </div>
          <div className="h-4 w-16 bg-gray-200 rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-gray-200 rounded"></div>
          <div className="h-3 w-full bg-gray-200 rounded"></div>
          <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
        </div>
      </div>
    ));
  };

  return (
    <div className="flex flex-col w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 px-4 lg:px-0">
        <h3 className="text-[16px] md:text-[20px] font-myYekanFaNumDemiBold">نظرات کاربران</h3>
      </div>

      {/* Mobile & Tablet View: Horizontal Scroll (Slider) */}
      <div className="lg:hidden relative font-myYekanFaNumRegular mb-2">
        <div className="px-0 relative">
          <div className="flex overflow-x-auto pb-6 space-x-2 rtl:space-x-reverse scroll-smooth px-2 scrollbar-hide">
            {isLoading && reviews.length === 0 ? (
              renderMobileSkeletons()
            ) : reviews.length === 0 ? (
              <div className="w-full text-center py-8 text-gray-500 font-myYekanFaNumRegular">
                نظری ثبت نشده است
              </div>
            ) : (
              reviews.map((review) => (
                <div
                  key={review.review_id}
                  className="min-w-[313px] w-[343px] h-[200px] rounded-2xl border border-gray-200 p-[16px] flex flex-col justify-between shadow-sm bg-white flex-shrink-0"
                >
                  <div className="">
                    <StarRating
                      rating={review.rate}
                      user_name={review.user_name}
                      date={toJalali(review.date)}
                    />
                  </div>
                  
                  <p className="text-gray-800 leading-relaxed font-myYekanFaNumRegular text-justify flex-1 line-clamp-4 text-sm">
                    {review.comment}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Desktop View: Vertical List (Infinite Scroll) */}
      <div className="hidden lg:flex flex-col p-0">
        {isLoading && reviews.length === 0 ? (
          <div className="space-y-6">
            {[1, 2].map((item) => (
              <div key={item} className="animate-pulse bg-white p-4 rounded-md border border-gray-200">
                <div className="flex justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-4 w-16 bg-gray-200 rounded"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-full bg-gray-200 rounded"></div>
                  <div className="h-3 w-full bg-gray-200 rounded"></div>
                  <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="w-full text-center py-8 text-gray-500 font-myYekanFaNumRegular">
            نظری ثبت نشده است
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.review_id} className="bg-white p-4 rounded-md border border-gray-200">
                <div className="">
                  <StarRating rating={review.rate} user_name={review.user_name} date={toJalali(review.date)} />
                </div>
                <p className="text-gray-800 leading-relaxed font-myYekanFaNumRegular text-justify text-base">
                  {review.comment}
                </p>
              </div>
            ))}
            
            {/* Infinite scroll loader */}
            {currentPage < totalPages && (
              <div ref={loaderRef} className="text-center py-4 text-gray-500">
                {isLoading ? 'در حال بارگذاری...' : ''}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Hide scrollbar styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ReviewsSection;